// dependencies
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
// hooks
import useInput from '@digital_monorepo/lib-common/hooks/useInput';
import useRedux from '@/hooks/useRedux';
const LoginPage = () => {
  const { t } = useTranslation();
  const { login, getAuthData } = useRedux();
  const navigate = useNavigate();
  const { isLoggedIn } = getAuthData();
  const [formHasError, setFormHasError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);

  const { value: enteredEmail, isValid: enteredEmailIsValid, valueChangeHandler: emailChangedHandler, inputBlurHandler: emailBlurHandler } = useInput(value => value);

  const { value: enteredPassword, isValid: enteredPasswordIsValid, valueChangeHandler: passwordChangedHandler, inputBlurHandler: passwordBlurHandler } = useInput(value => !!value);

  let formIsValid = false;
  if (enteredEmailIsValid && enteredPasswordIsValid) {
    formIsValid = true;
  }

  const formSubmissionHandler = async event => {
    event.preventDefault();
    if (!formIsValid) {
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    const res = await login(enteredEmail, enteredPassword);
    setSubmitting(false);
    if (res?.token) {
      navigate('/');
    } else {
      setFormHasError(true);
      if (res?.status === 'PENDING') {
        setIsPending(true);
      }
      // A network/server problem is reported by auth-actions as { error: { type, status, message } }.
      // type === 'ERR_NETWORK' (axios) or status === 501 (our normalized BFF_DOWN) means the
      // request never got a real auth response — don't blame the user's credentials.
      const errType = res?.error?.type;
      const errStatus = res?.error?.status;
      if (errType === 'ERR_NETWORK' || errType === 'ECONNABORTED' || errStatus === 501) {
        setIsNetworkError(true);
      }
    }
  };

  return (
    <div className='workspace-login'>
      <section className='login-form' aria-labelledby='login-title'>
        <h1 id='login-title'>Scheduling</h1>
        {formHasError && (
          <div className='login-error' role='alert'>
            {isNetworkError ? t('LOGIN.errorMessageNetwork') : isPending ? t('LOGIN.errorMessagePending') : t('LOGIN.errorMessageInvalid')}
          </div>
        )}
        {!isLoggedIn ? (
          <form onSubmit={formSubmissionHandler}>
            <div className='login-field'>
              <label htmlFor='email'>Username</label>
              <input
                id='email'
                type='text'
                autoComplete='username'
                required
                value={enteredEmail}
                onChange={e => {
                  emailChangedHandler(e);
                  setFormHasError(false);
                  setIsPending(false);
                  setIsNetworkError(false);
                }}
                onBlur={emailBlurHandler}
              />
            </div>
            <div className='login-field'>
              <label htmlFor='password'>Password</label>
              <div className='login-password'>
                <input
                  id='password'
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='current-password'
                  required
                  value={enteredPassword}
                  onChange={e => {
                    passwordChangedHandler(e);
                    setFormHasError(false);
                    setIsPending(false);
                    setIsNetworkError(false);
                  }}
                  onBlur={passwordBlurHandler}
                />
                <button type='button' onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>
            <button className='workspace-button' type='submit' disabled={submitting}>
              {submitting ? 'Signing in…' : 'Login'}
            </button>
          </form>
        ) : (
          <button className='workspace-button' onClick={() => navigate('/')}>
            Open workspace
          </button>
        )}
      </section>
    </div>
  );
};
export default LoginPage;
